import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Copy, ArrowRight, Maximize2, ZoomIn, ZoomOut, Undo2, Redo2, Check, Save, MousePointer2, Hand, LayoutGrid } from 'lucide-react';
import NodeSettings, {nodeSummary} from './automation/NodeSettings';
import ExecutionHistory from './automation/ExecutionHistory';
import {NODE_TYPES, NODE_WIDTH, createNode, outputs, nodeHeight, portY, nodeName, fragment, insertFragment, removeNodes, connect, fitView, arrangeGraph} from './automation/graph';

async function api(url, method, body) {
  const response = await fetch(url, {method, headers: {Accept:'application/json','Content-Type':'application/json',
    'X-CSRF-Token':document.querySelector('[name="csrf-token"]')?.content}, body: body ? JSON.stringify(body) : undefined});
  const type = response.headers.get('content-type') || '';
  if (!type.includes('application/json')) throw new Error('The server could not complete this request. Reload the page and try again.');
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.errors?.join('. ') || 'Could not complete the request');
    error.data = data;
    throw error;
  }
  return data;
}
const clone = (value) => JSON.parse(JSON.stringify(value));
const Icon = ({type: Type}) => <Type size={15} strokeWidth={1.6} />;

export default function AutomationChainBuilder({chain, graph: initialGraph, events = [], segments: initialSegments = [], attributes = [],
  smsTemplates = [], emailTemplates = [], emailSenders = [], copyTargets = []}) {
  const [graph, setGraph] = useState(initialGraph);
  const [name, setName] = useState(chain.name);
  const [revision, setRevision] = useState(chain.graph_revision);
  const [baseline, setBaseline] = useState(JSON.stringify({graph:initialGraph,name:chain.name}));
  const [selection, setSelection] = useState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [view, setView] = useState({x:40,y:60,zoom:1});
  const [mode, setMode] = useState('select');
  const [pendingConnection, setPendingConnection] = useState(null);
  const [marquee, setMarquee] = useState(null);
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [libraries, setLibraries] = useState({});
  const [segments, setSegments] = useState(initialSegments);
  const [copyTarget, setCopyTarget] = useState('');
  const [copyModal, setCopyModal] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [editWarning, setEditWarning] = useState(null);
  const [status, setStatus] = useState(chain.status);
  const [startReview, setStartReview] = useState(null);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const viewport = useRef(null);
  const gesture = useRef(null);
  const copyDialog = useRef(null);
  const editDialog = useRef(null);
  const confirmation = useRef(null);
  const dirty = JSON.stringify({graph,name}) !== baseline;
  const readOnly = Boolean(chain.archived_at);
  const locked = readOnly || busy || Boolean(startReview);
  const chosen = graph.nodes.find((node) => selection.length === 1 && node.id === selection[0]);
  const options = {events,segments,smsTemplates,emailTemplates,emailSenders,byAttribute:Object.fromEntries(attributes.map((attribute) => [attribute.id,attribute]))};

  const remember = () => { setHistory((items) => [...items.slice(-49),clone(graph)]); setFuture([]); };
  const change = (next, record = true) => {
    if (locked) return;
    if (record) remember();
    setGraph(next); setIssues([]); setNotice('');
  };
  const fit = () => {
    if (!viewport.current) return;
    const bounds = viewport.current.getBoundingClientRect();
    setView(fitView(graph,bounds.width,bounds.height));
  };
  useEffect(() => {
    const nodeId = new URLSearchParams(window.location.search).get('node');
    if (graph.nodes.some((node) => node.id === nodeId)) reveal(nodeId);
    else fit();
  }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload',warn);
    return () => window.removeEventListener('beforeunload',warn);
  }, [dirty]);
  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const pairs = await Promise.all(['trigger','filter','branch','exit'].map(async (purpose) => {
          const response = await fetch(`/api/v1/attributes?for=automation_${purpose}`,{headers:{Accept:'application/json'},signal:controller.signal});
          if (!response.ok) throw new Error('Could not load attributes. Reload the page to retry.');
          return [purpose,await response.json()];
        }));
        setLibraries(Object.fromEntries(pairs));
      } catch (err) { if (err.name !== 'AbortError') setError(err.message); }
    };
    load();
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (copyModal) copyDialog.current.showModal();
  }, [copyModal]);
  useEffect(() => { if (editWarning) editDialog.current.showModal(); }, [editWarning]);
  useEffect(() => () => confirmation.current?.(false), []);

  const answerWarning = (accepted) => {
    setEditWarning(null); confirmation.current?.(accepted); confirmation.current = null;
  };
  const editRequest = async (url, method, body) => {
    try { return await api(url,method,body); }
    catch (err) {
      if (err.data?.code !== 'structure_confirmation_required') throw err;
      const accepted = await new Promise((resolve) => { confirmation.current = resolve; setEditWarning(err.message); });
      if (!accepted) { const cancelled = new Error(); cancelled.cancelled = true; throw cancelled; }
      return api(url,method,{...body,confirm_structure_change:true,target_revision:err.data.confirmation_revision});
    }
  };

  const undo = () => {
    if (!history.length || locked) return;
    setFuture((items) => [clone(graph),...items]); setGraph(history[history.length - 1]); setHistory((items) => items.slice(0,-1));
    setSelection([]); setIssues([]);
  };
  const redo = () => {
    if (!future.length || locked) return;
    setHistory((items) => [...items,clone(graph)]); setGraph(future[0]); setFuture((items) => items.slice(1)); setSelection([]); setIssues([]);
  };
  const remove = () => {
    if (selectedEdge) { change({...graph,edges:graph.edges.filter((edge) => edge.id !== selectedEdge)}); setSelectedEdge(null); return; }
    change(removeNodes(graph,selection)); setSelection([]);
  };
  const duplicate = () => {
    if (!selection.length) return;
    const result = insertFragment(graph,fragment(graph,selection)); change(result.graph); setSelection(result.ids);
  };
  const copy = () => {
    if (!selection.length) return;
    sessionStorage.setItem('automation-node-selection',JSON.stringify(fragment(graph,selection)));
    setNotice(`${selection.length} nodes copied. Open another chain and paste, or use Copy to chain.`);
  };
  const paste = () => {
    try {
      const part = JSON.parse(sessionStorage.getItem('automation-node-selection'));
      if (!part?.nodes?.length) return;
      const result = insertFragment(graph,part); change(result.graph); setSelection(result.ids);
    } catch (_) { setError('Could not paste nodes. Copy the selection again.'); }
  };
  useEffect(() => {
    const handle = (event) => {
      if (event.target.closest('input,select,textarea,[contenteditable="true"],dialog')) return;
      const command = event.metaKey || event.ctrlKey;
      if (event.key === 'Escape') { setPendingConnection(null); setSelection([]); setSelectedEdge(null); return; }
      if (command && event.key.toLowerCase() === 'a') { event.preventDefault(); setSelection(graph.nodes.map((node) => node.id)); return; }
      if (command && event.key.toLowerCase() === 'c') { event.preventDefault(); copy(); return; }
      if (locked) return;
      if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); remove(); }
      if (command && event.key.toLowerCase() === 'd') { event.preventDefault(); duplicate(); }
      if (command && event.key.toLowerCase() === 'v') { event.preventDefault(); paste(); }
      if (command && event.key.toLowerCase() === 'z') { event.preventDefault(); event.shiftKey ? redo() : undo(); }
    };
    const target = viewport.current?.closest('.automation-chain-builder');
    target?.addEventListener('keydown',handle);
    return () => target?.removeEventListener('keydown',handle);
  });

  const add = (type) => {
    const bounds = viewport.current.getBoundingClientRect();
    const position = chosen ? {x:chosen.position.x + 310,y:chosen.position.y}
      : {x:Math.round((bounds.width / 2 - view.x) / view.zoom - NODE_WIDTH / 2),y:Math.round((bounds.height / 2 - view.y) / view.zoom - 50)};
    // Небольшое смещение, если в центре уже есть узел.
    while (graph.nodes.some((node) => Math.abs(node.position.x - position.x) < 30 && Math.abs(node.position.y - position.y) < 30)) { position.x += 36; position.y += 36; }
    const node = createNode(type,position); change({...graph,nodes:[...graph.nodes,node]}); setSelection([node.id]); setSelectedEdge(null);
    if ((position.x + NODE_WIDTH) * view.zoom + view.x > bounds.width - 20 || position.y * view.zoom + view.y > bounds.height - 60) {
      setView({...view,x:bounds.width/2-(position.x+NODE_WIDTH/2)*view.zoom,y:bounds.height/2-(position.y+60)*view.zoom});
    }
  };
  const select = (nodeId, additive) => {
    setSelectedEdge(null);
    setSelection((ids) => additive ? ids.includes(nodeId) ? ids.filter((value) => value !== nodeId) : [...ids,nodeId] : [nodeId]);
  };
  const link = (source,port,target) => { change(connect(graph,source,port,target)); setPendingConnection(null); };
  const patch = (node) => {
    const allowed = outputs(node).map((output) => output.id);
    change({nodes:graph.nodes.map((item) => item.id === node.id ? node : item),
      edges:graph.edges.filter((edge) => edge.source !== node.id || allowed.includes(edge.port))});
  };
  const graphPoint = (event) => {
    const bounds = viewport.current.getBoundingClientRect();
    return {x:(event.clientX - bounds.left - view.x) / view.zoom,y:(event.clientY - bounds.top - view.y) / view.zoom};
  };
  const startNodeDrag = (event,node) => {
    if (event.button !== 0 || event.target.closest('button,input,a,select')) return;
    if (pendingConnection) { if (!locked && node.type !== 'trigger' && node.id !== pendingConnection.source) link(pendingConnection.source,pendingConnection.port,node.id); return; }
    event.stopPropagation();
    const additive = event.shiftKey || event.metaKey || event.ctrlKey;
    if (additive) { select(node.id,true); return; }
    const ids = selection.includes(node.id) ? selection : [node.id];
    setSelection(ids); setSelectedEdge(null);
    if (locked) return;
    remember();
    gesture.current = {type:'nodes',pointerId:event.pointerId,start:{x:event.clientX,y:event.clientY},ids,original:clone(graph)};
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const startBackground = (event) => {
    if (event.target.closest('.graph-node,.graph-edge,.graph-canvas-tools') || event.button > 1) return;
    const point = graphPoint(event);
    if (mode === 'pan' || event.button === 1 || event.altKey) {
      gesture.current = {type:'pan',start:{x:event.clientX,y:event.clientY},view:{...view}};
    } else {
      if (!(event.shiftKey || event.metaKey || event.ctrlKey)) setSelection([]);
      setSelectedEdge(null); setPendingConnection(null);
      gesture.current = {type:'marquee',start:point,initial:event.shiftKey || event.metaKey || event.ctrlKey ? selection : []};
      setMarquee({x:point.x,y:point.y,width:0,height:0});
    }
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = (event) => {
    const active = gesture.current;
    if (!active) return;
    if (active.type === 'nodes') {
      const dx = (event.clientX - active.start.x) / view.zoom, dy = (event.clientY - active.start.y) / view.zoom;
      setGraph({...active.original,nodes:active.original.nodes.map((node) => active.ids.includes(node.id)
        ? {...node,position:{x:Math.round(node.position.x + dx),y:Math.round(node.position.y + dy)}} : node)});
      setIssues([]);
    } else if (active.type === 'pan') {
      setView({...active.view,x:active.view.x + event.clientX - active.start.x,y:active.view.y + event.clientY - active.start.y});
    } else {
      const point = graphPoint(event);
      const box = {x:Math.min(active.start.x,point.x),y:Math.min(active.start.y,point.y),width:Math.abs(point.x-active.start.x),height:Math.abs(point.y-active.start.y)};
      setMarquee(box);
      const ids = graph.nodes.filter((node) => node.position.x + NODE_WIDTH >= box.x && node.position.x <= box.x + box.width && node.position.y + nodeHeight(node) >= box.y && node.position.y <= box.y + box.height).map((node) => node.id);
      setSelection(Array.from(new Set([...active.initial,...ids])));
    }
  };
  const finish = () => { gesture.current = null; setMarquee(null); };
  const save = async () => {
    const data = await editRequest(`/automation-chains/${chain.id}/graph`,'PATCH',{name,revision,graph_definition:graph});
    setRevision(data.graph_revision); setBaseline(JSON.stringify({graph,name})); setNotice('Diagram saved.'); return data;
  };
  const saveClick = async () => {
    if (locked) return;
    setBusy(true); setError('');
    try { await save(); } catch (err) { if (!err.cancelled) setError(err.message); }
    finally { setBusy(false); }
  };
  const check = async (review = false) => {
    if (busy || readOnly && review) return;
    setBusy(true); setError('');
    try {
      const data = await api(`/automation-chains/${chain.id}/validate_graph`,'POST',{graph_definition:graph});
      setIssues(data.issues); setNotice(data.issues.length ? `${data.issues.length} issues found. Select an issue to inspect its node.` : 'Diagram is valid.');
      if (review && !data.issues.length) {
        const saved = dirty ? await save() : {graph_revision:revision};
        fit(); setSelection([]); setStartReview({revision:saved.graph_revision});
      }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const activate = async () => {
    if (busy || !startReview) return;
    setBusy(true); setError('');
    try {
      const data = await api(`/automation-chains/${chain.id}/activate`,'POST',{revision:startReview.revision});
      setStatus(data.status); setStartReview(null); setNotice('Chain is active. New players can now enter.');
    } catch (err) {
      setStartReview(null); setIssues(err.data?.issues || []); setError(err.message);
    } finally { setBusy(false); }
  };
  const copyToChain = async () => {
    if (!copyTarget || busy) return;
    setBusy(true); setError('');
    try {
      const saved = dirty ? await save() : {graph_revision:revision};
      const result = await editRequest(`/automation-chains/${chain.id}/copy_nodes`,'POST',{target_id:copyTarget,revision:saved.graph_revision,node_ids:selection});
      setCopyModal(false); setNotice(`${result.copied} nodes copied to “${result.name}”.`);
    } catch (err) { if (!err.cancelled) setError(err.message); }
    finally { setBusy(false); }
  };
  const reveal = (nodeId) => {
    const node = graph.nodes.find((item) => item.id === nodeId);
    if (!node) return;
    setSelection([nodeId]);
    const bounds = viewport.current.getBoundingClientRect();
    setView({zoom:1,x:bounds.width/2-node.position.x-NODE_WIDTH/2,y:bounds.height/2-node.position.y-70});
  };

  return <main className='automation-chain-builder'>
    <header className='graph-header'>
      <a href='/automation-chains' data-turbolinks='false'>← Chains</a>
      <input className='graph-name' aria-label='Chain name' value={name} disabled={locked} onChange={(event) => setName(event.target.value)} />
      <span className='graph-save-state'>{readOnly ? 'Archived · Read only' : dirty ? 'Unsaved changes' : 'Saved'}</span>
      <button className='chain-button' onClick={() => setShowParticipants(true)}>Participants</button>
      <a className='chain-button' href={`/automation-chains/${chain.id}/results`}>Results</a>
      <a className='chain-button' href={`/automation-chains/${chain.id}/split_results`}>Split-test results</a>
      <a className='chain-button' href={`/analytics/automations/${chain.id}`}>Analytics</a>
      <a className='chain-button' href={`/automation-chains/${chain.id}/segment_results`}>Segment results</a>
      <button className='chain-button' disabled={locked || !graph.nodes.length} onClick={() => {
        const next = arrangeGraph(graph); change(next);
        const bounds = viewport.current.getBoundingClientRect(); setView(fitView(next,bounds.width,bounds.height));
      }}><Icon type={LayoutGrid} />Arrange</button>
      <button className='chain-button' disabled={busy || Boolean(startReview)} onClick={() => check()}><Icon type={Check} />Check diagram</button>
      {!readOnly && <button className='chain-button chain-button--primary' disabled={locked || !dirty} onClick={saveClick}><Icon type={Save} />{busy ? 'Working…' : 'Save diagram'}</button>}
      {!readOnly && status !== 'active' && <button className='chain-button chain-button--primary' disabled={locked} onClick={() => check(true)}>{status === 'paused' ? 'Review and resume' : 'Review and start'}</button>}
    </header>
    {['active','paused'].includes(status) && <p className='graph-warning'>Existing players continue on their published version, keeping their history and completed actions. Saved diagram changes apply to new players after the next start or resume.</p>}
    {startReview && <section className='graph-start-review' aria-label='Review before activation'>
      <div><h2>Review before activation</h2><p>All checks passed. Review the complete diagram below. Starting allows new players to enter this saved scenario.</p></div>
      <button className='chain-button' disabled={busy} onClick={() => setStartReview(null)}>Back to editing</button>
      <button className='chain-button chain-button--primary' disabled={busy} onClick={activate}>{busy ? 'Starting…' : status === 'paused' ? 'Resume chain' : 'Start chain'}</button>
    </section>}
    {error && <p role='alert' className='chain-error graph-message'>{error}</p>}
    {notice && <p role='status' className='graph-message'>{notice}</p>}
    <div className='graph-workspace'>
      <aside className='graph-palette' aria-label='Node types'>
        <h2>Add a node</h2>
        {Object.entries(NODE_TYPES).map(([type,label]) => <button className={`graph-palette-item graph-type--${type}`} key={type} disabled={locked} onClick={() => add(type)}><Icon type={Plus} />{label}</button>)}
        <p>Drag nodes to move them. Shift-click or drag a selection box to select a group.</p>
      </aside>
      <section className='graph-center'>
        <div className='graph-selection-bar'>
          <select aria-label='Find node' value='' onChange={(event) => reveal(event.target.value)}>
            <option value=''>Find node…</option>{graph.nodes.map((node) => <option key={node.id} value={node.id}>{nodeName(node)} · {node.id.slice(0,6)}</option>)}
          </select>
          <span>{selection.length} selected</span>
          <button className='chain-button' disabled={!graph.nodes.length} onClick={() => {setSelection(graph.nodes.map((node) => node.id));setSelectedEdge(null);}}>Select all</button>
          <button className='chain-button' disabled={!selection.length} onClick={() => setSelection([])}>Clear selection</button>
          <button className='chain-button' disabled={locked || !selection.length} onClick={duplicate}><Icon type={Copy} />Duplicate</button>
          <button className='chain-button' disabled={!selection.length} onClick={copy}>Copy</button>
          <button className='chain-button' disabled={locked} onClick={paste}>Paste</button>
          <button className='chain-button' disabled={!selection.length || !copyTargets.length || busy} onClick={() => setCopyModal(true)}><Icon type={ArrowRight} />Copy to chain</button>
          <button className='chain-button chain-danger' disabled={locked || (!selection.length && !selectedEdge)} onClick={remove}><Icon type={Trash2} />Delete</button>
        </div>
        {pendingConnection && <div className='graph-connection-hint'>Select a target node or its input to connect {pendingConnection.port}. <button className='chain-button' onClick={() => setPendingConnection(null)}>Cancel connection</button></div>}
        <div ref={viewport} className={`graph-viewport graph-mode--${mode}`} role='application' aria-label='Chain diagram' tabIndex={0}
          onPointerDown={startBackground} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish}
          onWheel={(event) => { if (event.ctrlKey || event.metaKey) setView((current) => ({...current,zoom:Math.max(.001,Math.min(2,current.zoom*(event.deltaY > 0 ? .9 : 1.1)))})); }}>
          <div className='graph-world' style={{transform:`translate(${view.x}px,${view.y}px) scale(${view.zoom})`}}>
            <svg className='graph-lines' width='1' height='1' aria-label='Connections'>
              <defs><marker id='graph-arrow' markerWidth='8' markerHeight='8' refX='7' refY='4' orient='auto'><path d='M0 0 L8 4 L0 8 Z' /></marker></defs>
              {graph.edges.map((edge,index) => {
                const source = graph.nodes.find((node) => node.id === edge.source), target = graph.nodes.find((node) => node.id === edge.target);
                if (!source || !target) return null;
                const x = source.position.x + NODE_WIDTH, y = source.position.y + portY(source,edge.port), tx = target.position.x, ty = target.position.y + 50;
                const bend = Math.max(70,Math.abs(tx-x)*.45);
                const lane = Math.max(y,ty)+120+(index%5)*20;
                const path = Math.abs(tx-x)>450
                  ? `M${x},${y} C${x+60},${y} ${x+60},${lane} ${x+120},${lane} L${tx-120},${lane} C${tx-60},${lane} ${tx-60},${ty} ${tx},${ty}`
                  : `M${x},${y} C${x+bend},${y} ${tx-bend},${ty} ${tx},${ty}`;
                const label = outputs(source).find((port) => port.id === edge.port)?.label || edge.port;
                return <g className={`graph-edge${selectedEdge === edge.id ? ' graph-edge--selected' : ''}`} key={edge.id} onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {event.stopPropagation();setSelectedEdge(edge.id);setSelection([]);}}>
                  <path d={path} className='graph-edge-hit' /><path d={path} className='graph-edge-line' markerEnd='url(#graph-arrow)' />
                  <text x={x+18} y={y-9}>{label}</text>
                </g>;
              })}
            </svg>
            {graph.nodes.map((node) => <article key={node.id} data-node-id={node.id} aria-label={`${nodeName(node)} node`}
              className={`graph-node graph-type--${node.type}${selection.includes(node.id) ? ' graph-node--selected' : ''}${issues.some((issue) => issue.node_id === node.id) ? ' graph-node--invalid' : ''}`}
              style={{left:node.position.x,top:node.position.y,width:NODE_WIDTH,minHeight:nodeHeight(node)}}
              onPointerDown={(event) => startNodeDrag(event,node)} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish}>
              {node.type !== 'trigger' && <button className='graph-input' aria-label={`Connect to ${nodeName(node)}`} disabled={locked || !pendingConnection || pendingConnection.source === node.id}
                onClick={(event) => {event.stopPropagation();if(pendingConnection)link(pendingConnection.source,pendingConnection.port,node.id);}} />}
              <header><input type='checkbox' aria-label={`Select ${nodeName(node)}`} checked={selection.includes(node.id)} onChange={() => select(node.id,true)} />
                <button className='graph-node-title' onClick={() => {
                  if (pendingConnection && !locked && node.type !== 'trigger' && pendingConnection.source !== node.id) link(pendingConnection.source,pendingConnection.port,node.id);
                  else if (!pendingConnection) select(node.id,false);
                }}>{nodeName(node)}</button></header>
              <p className='graph-node-kind'>{NODE_TYPES[node.type]}{node.type === 'trigger' ? ' · START' : node.type === 'exit' ? ' · END' : ''}</p>
              <p className='graph-node-summary'>{nodeSummary(node,options)}</p>
              <div className='graph-outputs'>{outputs(node).map((port) => <button key={port.id} disabled={locked}
                aria-label={`Connect ${nodeName(node)} ${port.label}`} className={pendingConnection?.source === node.id && pendingConnection.port === port.id ? 'is-connecting' : ''}
                onClick={(event) => {event.stopPropagation();setPendingConnection({source:node.id,port:port.id});}}>{port.label}<span /></button>)}</div>
            </article>)}
            {marquee && <div className='graph-marquee' style={{left:marquee.x,top:marquee.y,width:marquee.width,height:marquee.height}} />}
          </div>
          {!graph.nodes.length && <div className='graph-canvas-empty'>Add a Trigger to start building your chain.</div>}
          <div className='graph-canvas-tools'>
            <button className={`chain-button${mode === 'select' ? ' graph-tool-active' : ''}`} aria-label='Selection tool' onClick={() => setMode('select')}><Icon type={MousePointer2} /></button>
            <button className={`chain-button${mode === 'pan' ? ' graph-tool-active' : ''}`} aria-label='Pan tool' onClick={() => setMode('pan')}><Icon type={Hand} /></button>
            <button className='chain-button' aria-label='Zoom out' onClick={() => setView({...view,zoom:Math.max(.001,view.zoom/1.2)})}><Icon type={ZoomOut} /></button>
            <span>{view.zoom < .1 ? (view.zoom*100).toFixed(1) : Math.round(view.zoom*100)}%</span>
            <button className='chain-button' aria-label='Zoom in' onClick={() => setView({...view,zoom:Math.min(2,view.zoom*1.2)})}><Icon type={ZoomIn} /></button>
            <button className='chain-button' onClick={fit}><Icon type={Maximize2} />Fit all</button>
            <button className='chain-button' aria-label='Undo' disabled={locked || !history.length} onClick={undo}><Icon type={Undo2} /></button>
            <button className='chain-button' aria-label='Redo' disabled={locked || !future.length} onClick={redo}><Icon type={Redo2} /></button>
          </div>
        </div>
        {issues.length > 0 && <section className='graph-issues' aria-label='Diagram issues'><h2>Check diagram</h2>
          {issues.map((issue,index) => <button key={index} className='graph-issue' onClick={() => reveal(issue.node_id)}>
            {graph.nodes.find((node) => node.id === issue.node_id) ? `${nodeName(graph.nodes.find((node) => node.id === issue.node_id))}: ` : ''}{issue.message}
          </button>)}
        </section>}
      </section>
      {chosen ? <NodeSettings key={chosen.id} node={chosen} graph={graph} options={options} libraries={libraries} patch={patch} connectOutput={link} readOnly={locked}
        onSegmentCreated={(segment) => setSegments((items) => [...items,segment])} />
        : <aside className='graph-inspector'><h2>{selection.length > 1 ? `${selection.length} nodes selected` : selectedEdge ? 'Connection selected' : 'Node settings'}</h2>
          <p>{selection.length > 1 ? 'Drag any selected node to move the group. Duplicate, delete or copy the group using the toolbar.' : selectedEdge ? 'Press Delete or use the toolbar to remove this connection.' : 'Select a node to view its settings. Connect an output to another node, or choose the next step in the settings panel.'}</p>
        </aside>}
    </div>
    {copyModal && <dialog ref={copyDialog} className='automation-chains-dialog' aria-labelledby='copy-nodes-title'
      onCancel={(event) => {event.preventDefault();if(!busy)setCopyModal(false);}}>
      <h2 id='copy-nodes-title'>Copy {selection.length} nodes to another chain</h2>
      <p>Settings and connections within the selection are copied. Original nodes remain in this chain.</p>
      <label className='graph-copy-label'>Destination chain<select value={copyTarget} onChange={(event) => setCopyTarget(event.target.value)}>
        <option value=''>Choose a chain</option>{copyTargets.map((target) => <option key={target.id} value={target.id}>{target.name}</option>)}
      </select></label>
      {error && <p role='alert' className='chain-error'>{error}</p>}
      <footer><button className='chain-button' disabled={busy} onClick={() => setCopyModal(false)}>Cancel</button>
        <button className='chain-button chain-button--primary' disabled={busy || !copyTarget} onClick={copyToChain}>{busy ? 'Copying…' : 'Copy nodes'}</button></footer>
    </dialog>}
    {showParticipants && <ExecutionHistory chainId={chain.id} onClose={() => setShowParticipants(false)} />}
    {editWarning && <dialog ref={editDialog} className='automation-chains-dialog' aria-labelledby='edit-warning-title'
      onCancel={(event) => {event.preventDefault();answerWarning(false);}}>
      <h2 id='edit-warning-title'>Save scenario changes</h2>
      <p>{editWarning}</p>
      <footer><button className='chain-button' onClick={() => answerWarning(false)}>Keep editing</button>
        <button className='chain-button chain-button--primary' onClick={() => answerWarning(true)}>Save changes</button></footer>
    </dialog>}
  </main>;
}
